import type { Product } from '@scayle/storefront-nuxt'

export interface ProductClickData {
  product_id: number
  item_list_id: string
  item_list_name: string
  timestamp: number
}

export interface ProductClickStorage {
  [productId: string]: ProductClickData
}

export interface ProductClickStorageOptions {
  product: Product
  listId: string
  listName: string
}

export interface CategoryContext {
  categoryName?: string
}

export interface SearchContext {
  searchTerm?: string
}

export function useProductClickStorage() {
  const STORAGE_KEY = 'snipes_product_click_data'

  /**
   * Get all product click data from localStorage
   */
  const getProductClickStorage = (): ProductClickStorage => {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      return data ? JSON.parse(data) : {}
    } catch (error) {
      console.warn(
        'Failed to retrieve product click storage from localStorage:',
        error,
      )
      return {}
    }
  }

  /**
   * Save product click data to localStorage
   */
  const saveProductClick = ({
    product,
    listId,
    listName,
  }: ProductClickStorageOptions) => {
    try {
      // Get existing storage or create empty object
      const existingData = getProductClickStorage()

      // Create new click data for this product
      const clickData: ProductClickData = {
        product_id: product.id,
        item_list_id: listId,
        item_list_name: listName,
        timestamp: Date.now(),
      }

      // Update or add this product's data
      existingData[product.id.toString()] = clickData

      // Save back to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData))
    } catch (error) {
      console.warn('Failed to save product click data to localStorage:', error)
    }
  }

  /**
   * Get product click data for a specific product ID
   */
  const getProductClick = (productId: number): ProductClickData | null => {
    try {
      const storage = getProductClickStorage()
      return storage[productId.toString()] || null
    } catch (error) {
      console.warn(
        'Failed to retrieve product click data from localStorage:',
        error,
      )
      return null
    }
  }

  /**
   * Get all product click data from localStorage
   */
  const getAllProductClicks = (): ProductClickData[] => {
    try {
      const storage = getProductClickStorage()
      return Object.values(storage)
    } catch (error) {
      console.warn(
        'Failed to retrieve product click data from localStorage:',
        error,
      )
      return []
    }
  }

  /**
   * Get the most recently clicked product
   */
  const getLastProductClick = (): ProductClickData | null => {
    try {
      const allClicks = getAllProductClicks()
      if (allClicks.length === 0) {
        return null
      }

      // Sort by timestamp descending and return the most recent
      return allClicks.sort((a, b) => b.timestamp - a.timestamp)[0]
    } catch (error) {
      console.warn(
        'Failed to retrieve last product click from localStorage:',
        error,
      )
      return null
    }
  }

  /**
   * Clear all product click data from localStorage
   */
  const clearAllProductClicks = () => {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.warn(
        'Failed to clear product click data from localStorage:',
        error,
      )
    }
  }

  /**
   * Clear a specific product's click data
   */
  const clearProductClick = (productId: number) => {
    try {
      const storage = getProductClickStorage()
      const productKey = productId.toString()
      if (productKey in storage) {
        // Create new object without the target key to avoid dynamic delete
        const newStorage = Object.fromEntries(
          Object.entries(storage).filter(([key]) => key !== productKey),
        )
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newStorage))
      }
    } catch (error) {
      console.warn(
        'Failed to clear specific product click data from localStorage:',
        error,
      )
    }
  }

  /**
   * Save product click from category list (PLP)
   */
  const saveProductClickFromCategory = (
    product: Product,
    context: CategoryContext,
  ) => {
    saveProductClick({
      product,
      listId: 'Category list',
      listName: `Category list: ${context.categoryName || 'Unknown'}`,
    })
  }

  /**
   * Save product click from search results
   */
  const saveProductClickFromSearch = (
    product: Product,
    context: SearchContext,
  ) => {
    saveProductClick({
      product,
      listId: 'Search result list',
      listName: `Search result list: ${context.searchTerm || 'Unknown'}`,
    })
  }

  /**
   * Save product click from recommendation carousel
   */
  const saveProductClickFromRecommendation = (
    product: Product,
    page = 'homepage',
  ) => {
    saveProductClick({
      product,
      listId: 'Product carousel',
      listName: `Reco: ${page}`,
    })
  }

  /**
   * Save product click from brand product list
   */
  const saveProductClickFromBrandList = (
    product: Product,
    context: CategoryContext,
  ) => {
    saveProductClick({
      product,
      listId: 'Brand products list',
      listName: `Brand products list: ${context.categoryName || 'Unknown'}`,
    })
  }

  /**
   * Save product click from wishlist
   */
  const saveProductClickFromWishlist = (product: Product) => {
    saveProductClick({
      product,
      listId: 'Wishlist',
      listName: 'Wishlist: individual list',
    })
  }

  return {
    // Core functions
    saveProductClick,
    getProductClick,
    getAllProductClicks,
    getLastProductClick,
    clearProductClick,
    clearAllProductClicks,

    // Context-specific helpers
    saveProductClickFromCategory,
    saveProductClickFromBrandList,
    saveProductClickFromSearch,
    saveProductClickFromRecommendation,
    saveProductClickFromWishlist,
  }
}
